import Vue from "vue";
import examApi from "../service/exam-api";

const vm = Vue.prototype;

export const state = () => ({
  questions: [],
  faculties: [],
  educationTypes: [],
  languages: [],
  sciences: [],
  stats: [],
});

export const mutations = {
  SET_QUESTIONS(state, payload) {
    state.questions = vm.$toArray(payload);
  },
  SET_FACULTIES(state, payload) {
    state.faculties = vm.$toArray(payload);
  },
  SET_EDUCATION_TYPES(state, payload) {
    state.educationTypes = vm.$toArray(payload);
  },
  SET_LANGUAGES(state, payload) {
    state.languages = vm.$toArray(payload);
  },
  SET_SCIENCES(state, payload) {
    state.sciences = vm.$toArray(payload);
  },
  SET_STATS(state, payload) {
    state.stats = vm.$toArray(payload);
  },
};

export const actions = {
  async INIT_QUESTIONS({ commit }, { facultyId, languageId, scienceId, search, page = 0, size = 20 }) {
    let totalItemsCount = 0;
    let query = `admin/exam/questions?page=${page}&size=${size}`;
    if (facultyId) query += `&facultyId=${facultyId}`;
    if (languageId) query += `&languageId=${languageId}`;
    if (scienceId) query += `&scienceId=${scienceId}`;
    if (search && search.trim()) query += `&search=${encodeURIComponent(search.trim())}`;

    try {
      const { data } = await examApi.get(query);
      if (data) {
        commit("SET_QUESTIONS", data.content);
        totalItemsCount = data.totalElements;
      }
    } catch (e) {
      console.error("INIT_QUESTIONS error:", e);
    }
    return totalItemsCount;
  },

  async GET_QUESTION(ctx, { id }) {
    try {
      const { data } = await examApi.get(`admin/exam/questions/${id}`);
      return data;
    } catch (e) {
      console.error("GET_QUESTION error:", e);
      return null;
    }
  },

  async INIT_FACULTIES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/faculties");
      if (data) {
        commit("SET_FACULTIES", data);
      }
      return data;
    } catch (e) {
      console.error("INIT_FACULTIES error:", e);
      return null;
    }
  },

  async INIT_EDUCATION_TYPES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/education-types");
      if (data) {
        commit("SET_EDUCATION_TYPES", data);
      }
      return data;
    } catch (e) {
      console.error("INIT_EDUCATION_TYPES error:", e);
      return null;
    }
  },

  async INIT_LANGUAGES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/languages");
      if (data) {
        commit("SET_LANGUAGES", data);
      }
      return data;
    } catch (e) {
      console.error("INIT_LANGUAGES error:", e);
      return null;
    }
  },

  async INIT_SCIENCES({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/sciences");
      if (data) {
        commit("SET_SCIENCES", data);
      }
      return data;
    } catch (e) {
      console.error("INIT_SCIENCES error:", e);
      return null;
    }
  },

  async SAVE_SCIENCE({ dispatch }, science) {
    try {
      const method = science.id ? "put" : "post";
      await examApi[method]("admin/exam/sciences", science);
      await dispatch("INIT_SCIENCES");
      return true;
    } catch (e) {
      console.error("SAVE_SCIENCE error:", e);
      return false;
    }
  },

  async DELETE_SCIENCE({ dispatch }, id) {
    try {
      await examApi.delete(`admin/exam/sciences/${id}`);
      await dispatch("INIT_SCIENCES");
      return true;
    } catch (e) {
      console.error("DELETE_SCIENCE error:", e);
      return false;
    }
  },

  async TOGGLE_SCIENCE({ dispatch }, { id, active }) {
    try {
      const method = active ? "activation" : "deactivation";
      await examApi.post(`admin/exam/sciences/${id}/${method}`);
      await dispatch("INIT_SCIENCES");
      return true;
    } catch (e) {
      console.error("TOGGLE_SCIENCE error:", e);
      return false;
    }
  },

  async INIT_STATS({ commit }) {
    try {
      const { data } = await examApi.get("admin/exam/stats");
      if (data) {
        commit("SET_STATS", data);
      }
      return data;
    } catch (e) {
      console.error("INIT_STATS error:", e);
      return null;
    }
  },

  async ADD_QUESTION(ctx, question) {
    try {
      const { data } = await examApi.post("admin/exam/questions", question);
      return data;
    } catch (e) {
      console.error("ADD_QUESTION error:", e);
      return null;
    }
  },

  async UPDATE_QUESTION(ctx, { id, question }) {
    try {
      const { data } = await examApi.put(`admin/exam/questions/${id}`, question);
      return data;
    } catch (e) {
      console.error("UPDATE_QUESTION error:", e);
      return null;
    }
  },

  async DELETE_QUESTION(ctx, { id }) {
    try {
      await examApi.delete(`admin/exam/questions/${id}`);
      return true;
    } catch (e) {
      console.error("DELETE_QUESTION error:", e);
      return false;
    }
  },

  async BULK_DELETE_QUESTIONS(ctx, { ids }) {
    try {
      const { data } = await examApi.post("admin/exam/questions/bulk-delete", ids);
      return data;
    } catch (e) {
      console.error("BULK_DELETE_QUESTIONS error:", e);
      return null;
    }
  },

  async DELETE_BY_FACULTY(ctx, { facultyId, languageId, scienceId }) {
    try {
      let query = `admin/exam/questions/by-faculty?facultyId=${facultyId}`;
      if (languageId) query += `&languageId=${languageId}`;
      if (scienceId) query += `&scienceId=${scienceId}`;
      const { data } = await examApi.delete(query);
      return data;
    } catch (e) {
      console.error("DELETE_BY_FACULTY error:", e);
      return null;
    }
  },

  async UPLOAD_EXCEL(ctx, { file, facultyId, languageId, scienceId }) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("facultyId", facultyId);
    formData.append("languageId", languageId);
    if (scienceId != null) formData.append("scienceId", scienceId);

    try {
      const { data } = await examApi.post("admin/exam/upload-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      return data;
    } catch (e) {
      console.error("UPLOAD_EXCEL error:", e);
      return null;
    }
  },
};

export const getters = {
  GET_QUESTIONS: (state) => state.questions,
  GET_FACULTIES: (state) => state.faculties,
  GET_EDUCATION_TYPES: (state) => state.educationTypes,
  GET_LANGUAGES: (state) => state.languages,
  GET_SCIENCES: (state) => state.sciences,
  GET_STATS: (state) => state.stats,
};
